import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  Download,
  ExternalLink,
  Instagram,
  Mail,
  MapPin,
  Menu,
  Play,
  X,
} from 'lucide-react';
import './App.css';
import {
  about,
  artists,
  events,
  exhibition,
  institutions,
  leadership,
  navigation,
  pressCoverage,
  site,
  team,
  venue,
} from './content';

const pageMeta = {
  '/': ['Somalia National Pavilion — SADDEXLEEY', site.description],
  '/exhibition': ['SADDEXLEEY — Exhibition', exhibition.introduction],
  '/artists': ['Exhibition Artists — Somalia National Pavilion', 'Ayan Farah, Asmaa Jama and Warsan Shire.'],
  '/programme': ['Programme — Somalia National Pavilion', 'Public programmes in Venice and Mogadishu.'],
  '/press': ['Press — Somalia National Pavilion', 'Verified coverage, releases and media resources.'],
  '/about': ['About — Somalia National Pavilion', 'Mandate, leadership, team, partners and visitor information.'],
};

function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '');
}

function useClientRouter() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((to) => {
    const nextPath = normalizePath(to);
    if (nextPath !== normalizePath(window.location.pathname)) {
      window.history.pushState({}, '', nextPath);
      setPath(nextPath);
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const artist = artists.find((item) => path === `/artists/${item.slug}`);
    const event = events.find((item) => path === `/programme/${item.slug}`);
    const rsvpEvent = events.find((item) => path === `/programme/${item.slug}/rsvp`);
    const [title, description] = artist
      ? [`${artist.name} — Somalia National Pavilion`, artist.role]
      : rsvpEvent
        ? [`RSVP — ${rsvpEvent.title}`, `Register for ${rsvpEvent.title} in Venice.`]
      : event
        ? [`${event.title} — Somalia National Pavilion`, event.summary]
        : pageMeta[path] || ['Somalia National Pavilion', site.description];

    document.title = title;
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) descriptionTag.setAttribute('content', description);
    const canonicalUrl = `https://www.somaliapavilion.so${path === '/' ? '/' : path}`;
    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) canonicalTag.setAttribute('href', canonicalUrl);
    const socialMeta = {
      'meta[property="og:title"]': title,
      'meta[property="og:description"]': description,
      'meta[property="og:url"]': canonicalUrl,
      'meta[name="twitter:title"]': title,
      'meta[name="twitter:description"]': description,
      'meta[property="twitter:url"]': canonicalUrl,
    };
    Object.entries(socialMeta).forEach(([selector, content]) => {
      const element = document.querySelector(selector);
      if (element) element.setAttribute('content', content);
    });
  }, [path]);

  return { path, navigate };
}

function Link({ to, navigate, className = '', children, ariaLabel }) {
  const handleClick = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };

  return (
    <a href={to} onClick={handleClick} className={className} aria-label={ariaLabel}>
      {children}
    </a>
  );
}

function Header({ path, navigate }) {
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [path]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', open);
    return () => document.body.classList.remove('menu-open');
  }, [open]);

  return (
    <>
      <header className="site-header">
        <a className="skip-link" href="#main-content">Skip to content</a>
        <div className="header-inner">
          <Link to="/" navigate={navigate} className="brand-link" ariaLabel="Somalia National Pavilion home">
            <img src="/favicon.svg" alt="" className="brand-mark" />
            <span>
              <strong>Somalia</strong>
              <small>National Pavilion</small>
            </span>
          </Link>

          <button
            type="button"
            className="menu-button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      {open && (
        <nav id="mobile-navigation" className="mobile-nav" aria-label="Primary navigation">
          {navigation.map((item, index) => (
            <Link
              key={item.to}
              to={item.to}
              navigate={navigate}
              className={path === item.to || path.startsWith(`${item.to}/`) ? 'active' : ''}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </>
  );
}

function Footer({ navigate }) {
  return (
    <footer className="site-footer">
      <div className="footer-lead">
        <div className="footer-identity">
          <p className="eyebrow light">Somalia · Venice · 2026</p>
          <h2>Somalia<br />National Pavilion</h2>
          <p>SADDEXLEEY · 61st International Art Exhibition</p>
        </div>
        <nav className="footer-navigation" aria-label="Footer navigation">
          <Link to="/" navigate={navigate}>Home</Link>
          {navigation.map((item) => <Link key={item.to} to={item.to} navigate={navigate}>{item.label}</Link>)}
        </nav>
        <div className="footer-contact">
          <a href={`mailto:${site.email}`}><Mail size={18} /> {site.email}</a>
          <a href={site.instagram} target="_blank" rel="noreferrer"><Instagram size={18} /> Instagram</a>
          <a href={site.x} target="_blank" rel="noreferrer"><ExternalLink size={18} /> X / Twitter</a>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="biennale-lockup">
          <img src="/assetsfolder/logo-61EIA-PartNaz-RGB.jpg" alt="La Biennale di Venezia, National Participations" />
        </div>
        <p>© 2026 Somalia National Pavilion. All rights reserved.</p>
      </div>
    </footer>
  );
}

function Layout({ path, navigate, children }) {
  return (
    <div className="app-shell">
      <Header path={path} navigate={navigate} />
      <main id="main-content">{children}</main>
      <Footer navigate={navigate} />
    </div>
  );
}

function SectionIntro({ eyebrow, title, description, inverted = false }) {
  return (
    <div className={`section-intro ${inverted ? 'inverted' : ''}`}>
      <p className={`eyebrow ${inverted ? 'light' : ''}`}>{eyebrow}</p>
      <h2>{title}</h2>
      {description && <p className="section-description">{description}</p>}
    </div>
  );
}

function LinkArrow({ external = false, size = 18 }) {
  const Icon = external ? ArrowUpRight : ArrowRight;
  return <span className="link-arrow" aria-hidden="true"><Icon size={size} /></span>;
}

function EventCard({ event, navigate, large = false }) {
  return (
    <article className={`event-card ${large ? 'event-card-large' : ''}`}>
      <div className="event-card-topline">
        <span>{event.status}</span>
      </div>
      <p className="eyebrow">{event.eyebrow}</p>
      <h3>{event.title}</h3>
      <div className="event-facts">
        <span><CalendarDays size={16} /> {event.date}</span>
        <span><MapPin size={16} /> {event.location}</span>
      </div>
      <p>{event.summary}</p>
      <Link to={`/programme/${event.slug}`} navigate={navigate} className="text-link">
        View programme <LinkArrow size={16} />
      </Link>
    </article>
  );
}

function PressRow({ item }) {
  return (
    <a className="press-row" href={item.url} target="_blank" rel="noreferrer">
      <div className="press-outlet">
        <strong>{item.outlet}</strong>
        <span>{item.date}</span>
      </div>
      <div>
        <h3>{item.title}</h3>
        <p>{item.description}</p>
      </div>
      <LinkArrow external />
    </a>
  );
}

function HomePage({ navigate }) {
  const upcoming = events.find((event) => event.status === 'Upcoming');
  const pastEvents = events.filter((event) => event.status === 'Past event').slice(0, 3);

  return (
    <>
      <section className="home-hero">
        <div className="hero-copy">
          <div className="hero-kicker">
            <p><strong>Somalia’s first National Pavilion</strong><small>Official participation · Venice Biennale 2026</small></p>
          </div>
          <h1>SADDEXLEEY</h1>
          <p className="hero-intro">Three artists. Three floors. Word, sound and matter in relation.</p>
          <div className="hero-actions">
            <Link to="/exhibition" navigate={navigate} className="button button-dark">Enter the exhibition <ArrowRight size={17} /></Link>
            <Link to="/programme" navigate={navigate} className="button button-outline">Explore the programme <ArrowRight size={17} /></Link>
          </div>
        </div>
        <div className="hero-identity" aria-hidden="true">
          <img src="/assetsfolder/BANNER_ONLINE.png" alt="" />
        </div>
        <div className="hero-meta">
          <span>{site.dates}</span>
          <span>{site.location}</span>
          <span>Free admission</span>
        </div>
      </section>

      <section className="statement-band">
        <p>Poetry becomes architecture.</p>
        <span>Meaning unfolds through repetition, variation and relation.</span>
      </section>

      <section className="page-section upcoming-section">
        <SectionIntro
          eyebrow="Next programme"
          title="Somali cinema in Venice"
          description="The public programme continues with a three-day platform for Somali storytelling during Venice’s closing festival weekend."
        />
        <EventCard event={upcoming} navigate={navigate} large />
      </section>

      <section className="page-section dark-section">
        <SectionIntro
          eyebrow="The exhibition"
          title="A triadic field"
          description={exhibition.introduction}
          inverted
        />
        <div className="movement-grid">
          {exhibition.movements.map((movement) => (
            <article key={movement.title} className="movement-card">
              <h3>{movement.title}</h3>
              <strong>{movement.artist}</strong>
              <p>{movement.text}</p>
            </article>
          ))}
        </div>
        <Link to="/exhibition" navigate={navigate} className="button button-light">Read the curatorial framework <ArrowRight size={17} /></Link>
      </section>

      <section className="page-section">
        <SectionIntro eyebrow="Exhibition artists" title="Three distinct practices" />
        <div className="artist-grid">
          {artists.map((artist) => (
            <Link key={artist.slug} to={`/artists/${artist.slug}`} navigate={navigate} className="artist-card">
              <div className="artist-image"><img src={artist.image} alt={`Portrait of ${artist.name}`} loading="lazy" /></div>
              <div><p>{artist.role}</p><h3>{artist.name}</h3><LinkArrow external /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="page-section muted-section">
        <div className="section-heading-row">
          <SectionIntro eyebrow="From Venice to Mogadishu" title="Programme archive" />
          <Link to="/programme" navigate={navigate} className="text-link">All programmes <LinkArrow size={16} /></Link>
        </div>
        <div className="event-grid">
          {pastEvents.map((event) => <EventCard key={event.slug} event={event} navigate={navigate} />)}
        </div>
      </section>

      <section className="page-section press-preview">
        <div className="section-heading-row">
          <SectionIntro eyebrow="Critical recognition" title="Selected coverage" />
          <Link to="/press" navigate={navigate} className="text-link">Full press archive <LinkArrow size={16} /></Link>
        </div>
        <div className="press-list">
          {pressCoverage.filter((item) => item.featured).slice(0, 4).map((item) => <PressRow key={`${item.outlet}-${item.title}`} item={item} />)}
        </div>
      </section>
    </>
  );
}

function ExhibitionPage({ navigate }) {
  return (
    <>
      <section className="page-hero exhibition-hero">
        <div>
          <p className="eyebrow">Exhibition · Venice 2026</p>
          <h1>{exhibition.title}</h1>
          <p className="pronunciation">Pronounced {exhibition.pronunciation}</p>
          <p className="page-hero-copy">{exhibition.introduction}</p>
        </div>
        <img src="/assetsfolder/saddexleey-identity.PNG.png" alt="SADDEXLEEY exhibition identity" />
      </section>

      <section className="page-section editorial-layout">
        <div className="editorial-aside">
          <p className="eyebrow">Curatorial framework</p>
          <p>Three artists<br />Three floors<br />Three registers</p>
        </div>
        <div className="editorial-body">
          {exhibition.statement.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </section>

      <section className="page-section dark-section">
        <SectionIntro eyebrow="Spatial rhythm" title="Word · Sound · Matter" inverted />
        <div className="movement-grid">
          {exhibition.movements.map((movement) => (
            <article key={movement.title} className="movement-card">
              <h3>{movement.title}</h3><strong>{movement.artist}</strong><p>{movement.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="page-section visit-panel">
        <div className="visit-image"><img src={venue.image} alt="Palazzo Caboto in Venice" loading="lazy" /></div>
        <div className="visit-content">
          <p className="eyebrow">Visit</p>
          <h2>{venue.name}</h2>
          <p><MapPin size={18} /> {venue.address}</p>
          <div className="schedule-list">
            {venue.schedule.map(([period, hours]) => <div key={period}><strong>{period}</strong><span>{hours}</span></div>)}
          </div>
          <a className="button button-dark" href="https://www.google.com/maps/dir/?api=1&destination=Palazzo+Caboto,+Castello+1645,+30122+Venezia+VE,+Italy" target="_blank" rel="noreferrer">Get directions <ArrowUpRight size={17} /></a>
        </div>
      </section>

      <section className="page-section next-page-link">
        <p>Continue exploring</p>
        <Link to="/artists" navigate={navigate}>Meet the artists <ArrowRight /></Link>
      </section>
    </>
  );
}

function ArtistsPage({ navigate }) {
  return (
    <>
      <section className="page-hero simple-hero">
        <div><p className="eyebrow">Exhibition artists</p><h1>Three Somali artists.<br />One relational field.</h1></div>
        <p className="page-hero-copy">SADDEXLEEY brings together Ayan Farah, Asmaa Jama and Warsan Shire as the exhibition artists of Somalia’s first National Pavilion. Commissioned by Somalia and grounded in Somali cultural agency, the exhibition gives each artist space to approach memory, movement and belonging through an independent practice.</p>
      </section>
      <section className="page-section artist-index">
        {artists.map((artist) => (
          <Link key={artist.slug} to={`/artists/${artist.slug}`} navigate={navigate} className="artist-index-row">
            <div className="artist-index-image"><img src={artist.image} alt={`Portrait of ${artist.name}`} loading="lazy" /></div>
            <div><p>{artist.role}</p><h2>{artist.name}</h2></div>
            <LinkArrow external />
          </Link>
        ))}
      </section>
    </>
  );
}

function ArtistPage({ artist, navigate }) {
  return (
    <>
      <section className="detail-hero artist-detail-hero">
        <div className="detail-image"><img src={artist.image} alt={`Portrait of ${artist.name}`} /></div>
        <div className="detail-copy">
          <Link to="/artists" navigate={navigate} className="back-link"><ChevronLeft size={18} /> All artists</Link>
          <p className="eyebrow">{artist.role}</p>
          <h1>{artist.name}</h1>
          <p>{artist.bio}</p>
        </div>
      </section>
      <section className="page-section holding-section">
        <p className="eyebrow">Exhibition record</p>
        <h2>Artwork documentation forthcoming.</h2>
      </section>
    </>
  );
}

function ProgrammePage({ navigate }) {
  const upcoming = events.filter((event) => event.status === 'Upcoming');
  const past = events.filter((event) => event.status === 'Past event');
  return (
    <>
      <section className="page-hero simple-hero">
        <div>
          <p className="eyebrow">Public programme</p>
          <h1 className="programme-connector-title">
            <span>Venice</span>
            <span className="programme-connector-icon" aria-hidden="true"><ArrowLeftRight /></span>
            <span>Mogadishu</span>
          </h1>
        </div>
        <p className="page-hero-copy">Screenings, exhibitions, performances and public encounters extending the Pavilion beyond the exhibition rooms.</p>
      </section>
      <section className="page-section upcoming-section">
        <SectionIntro eyebrow="Upcoming" title="Next programme" />
        <div className="event-grid single-column">
          {upcoming.map((event) => <EventCard key={event.slug} event={event} navigate={navigate} large />)}
        </div>
      </section>
      <section className="page-section muted-section">
        <SectionIntro eyebrow="Archive" title="Past programmes" />
        <div className="event-grid">
          {past.map((event) => <EventCard key={event.slug} event={event} navigate={navigate} />)}
        </div>
      </section>
    </>
  );
}

function VideoModule({ event }) {
  if (event.videoUrl) {
    return (
      <div className={`video-frame ${event.videoAspect === 'portrait' ? 'video-frame-portrait' : ''}`}>
        <iframe src={event.videoUrl} title={`${event.title} video highlight`} loading="lazy" referrerPolicy="strict-origin-when-cross-origin" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
      </div>
    );
  }
  return (
    <div className="media-placeholder">
      <Play size={42} />
      <div><strong>Video highlight reserved</strong><span>The final YouTube link will be added after upload and approval.</span></div>
    </div>
  );
}

function EventPage({ event, navigate }) {
  return (
    <>
      <section className="detail-hero event-detail-hero">
        <div className="event-detail-meta">
          <Link to="/programme" navigate={navigate} className="back-link"><ChevronLeft size={18} /> Programme</Link>
          <p className="eyebrow">{event.eyebrow}</p>
          <div className="event-status-line"><span>{event.status}</span></div>
          <h1>{event.title}</h1>
          <p>{event.summary}</p>
        </div>
        <div className="event-date-block">
          <div><CalendarDays /><span>Date</span><strong>{event.date}</strong></div>
          <div><MapPin /><span>Location</span><strong>{event.location}</strong></div>
        </div>
      </section>

      {event.rsvpUrl && (
        <section className="rsvp-callout">
          <div>
            <p className="eyebrow">Free entry · Limited capacity</p>
            <h2>Join the Somalia Pavilion Film Programme.</h2>
            <p>Registration takes less than a minute. Screening schedule and access details will be sent to confirmed attendees.</p>
          </div>
          <div className="rsvp-callout-actions">
            <Link to={`/programme/${event.slug}/rsvp`} navigate={navigate} className="button button-dark">
              RSVP for the programme <ArrowRight size={17} />
            </Link>
            {event.pressRelease && (
              <a className="button button-outline" href={event.pressRelease} download>
                Download press release <Download size={17} />
              </a>
            )}
          </div>
        </section>
      )}

      <section className="page-section editorial-layout">
        <div className="editorial-aside">
          <p className="eyebrow">Programme note</p>
          {event.curatedBy && <p>Curated by<br />{event.curatedBy.join(' · ')}</p>}
          {event.collaborators && <p>In collaboration with<br />{event.collaborators.join(' · ')}</p>}
          {event.partners && <p>With<br />{event.partners.join(' · ')}</p>}
        </div>
        <div className="editorial-body">{event.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
      </section>

      {event.exhibitionArtists && (
        <section className="page-section programme-record-section">
          <SectionIntro eyebrow="Inauguration record" title="Exhibition artists" />
          <div className="programme-name-list">
            {event.exhibitionArtists.map((name) => <strong key={name}>{name}</strong>)}
          </div>
        </section>
      )}

      {event.programmeArtists && (
        <section className="page-section muted-section programme-artists-section">
          <SectionIntro eyebrow="Opening programme" title="Programme and contributing artists" />
          <div className="programme-artist-grid">
            {event.programmeArtists.map((artist) => (
              <article key={artist.name}>
                <p>{artist.role}</p>
                <h3>{artist.name}</h3>
                <span>{artist.bio}</span>
              </article>
            ))}
          </div>
        </section>
      )}

      {event.screeningSchedule && (
        <section className="page-section screening-schedule-section">
          <SectionIntro eyebrow="Screening schedule" title="Two evenings of Somali cinema" description="Palazzo Caboto · Via Giuseppe Garibaldi 1645, Castello, Venice" />
          <div className="screening-night-grid">
            {event.screeningSchedule.map((night) => (
              <article key={night.date}>
                <header><p>{night.date}</p><strong>{night.time}</strong></header>
                <ol>
                  {night.films.map((title) => {
                    const film = event.films.find((item) => item.title === title);
                    return <li key={title}><span>{film.title}</span><small>{film.credit} · {film.runtime}</small></li>;
                  })}
                </ol>
                {night.note && <p className="screening-night-note">{night.note}</p>}
              </article>
            ))}
          </div>
        </section>
      )}

      {event.films && (
        <section className="page-section film-programme-section">
          <SectionIntro eyebrow="Selected through an open call" title="The films" />
          <div className="film-programme-grid">
            {event.films.map((film) => (
              <article key={film.title}>
                <div className="film-poster-frame"><img src={film.image} alt={`${film.title} film poster`} loading="lazy" /></div>
                <div className="film-card-copy"><p>{film.runtime}</p><h3>{film.title}</h3><span>{film.credit}</span></div>
              </article>
            ))}
          </div>
          {event.encounterNote && <p className="programme-encounter-note">{event.encounterNote}</p>}
        </section>
      )}

      {event.highlights && (
        <section className="page-section dark-section">
          <SectionIntro eyebrow="Programme" title="At a glance" inverted />
          <ul className="highlight-list">
            {event.highlights.map((highlight) => <li key={highlight}><strong>{highlight}</strong></li>)}
          </ul>
        </section>
      )}

      {event.pending && (
        <section className="page-section holding-section">
          <p className="eyebrow">Publication checklist</p>
          <h2>Full programme details forthcoming.</h2>
          <div className="pending-grid">{event.pending.map((item) => <span key={item}>{item}</span>)}</div>
        </section>
      )}

      {'videoUrl' in event && (
        <section className="page-section media-section">
          <SectionIntro eyebrow="Watch" title="Event highlight" />
          <VideoModule event={event} />
        </section>
      )}

      {event.images && event.images.length > 0 && (
        <section className="page-section gallery-section">
          <SectionIntro eyebrow="Documentation" title="Event gallery" description={`Public image credit: ${event.credit}`} />
          <div className="gallery-grid">
            {event.images.map((image, index) => (
              <figure key={image.src} className={index === 0 || index === 7 ? 'gallery-wide' : ''}>
                <img src={image.src} alt={image.alt} loading="lazy" />
                <figcaption>{image.credit}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {event.schoolVisit && (
        <section className="page-section school-visit-section">
          <SectionIntro eyebrow="Education" title={event.schoolVisit.title} description={`${event.schoolVisit.description} Public image credit: ${event.credit}`} />
          <div className="gallery-grid school-visit-grid">
            {event.schoolVisit.images.map((image, index) => (
              <figure key={image.src} className={index === 0 || index === 9 || index === 17 ? 'gallery-wide' : ''}>
                <img src={image.src} alt={image.alt} loading="lazy" />
                <figcaption>{image.credit}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function RsvpPage({ event, navigate }) {
  return (
    <>
      <section className="rsvp-page-hero">
        <div>
          <Link to={`/programme/${event.slug}`} navigate={navigate} className="back-link light-back-link">
            <ChevronLeft size={18} /> Programme details
          </Link>
          <p className="eyebrow light">Venice · September 2026</p>
          <h1>RSVP</h1>
          <p>{event.title}</p>
        </div>
        <div className="rsvp-page-facts">
          <span><CalendarDays size={18} /> {event.date}</span>
          <span><MapPin size={18} /> {event.location}</span>
          <strong>Free entry · Limited capacity</strong>
        </div>
      </section>

      <section className="rsvp-form-section">
        <div className="rsvp-form-intro">
          <p className="eyebrow">Register your interest</p>
          <h2>Four short questions.</h2>
          <p>Submitting this form does not guarantee a seat. Confirmed attendees will receive the screening schedule and access information by email.</p>
        </div>
        <div className="rsvp-form-shell">
          <iframe
            src={event.rsvpEmbedUrl}
            title={`RSVP for ${event.title}`}
            loading="eager"
            referrerPolicy="strict-origin-when-cross-origin"
          >
            Loading RSVP form…
          </iframe>
        </div>
        <div className="rsvp-form-footer">
          <p>Contact details are used only for this programme.</p>
          <a href={event.rsvpUrl} target="_blank" rel="noreferrer" className="text-link">
            Open the standalone form <LinkArrow external size={16} />
          </a>
        </div>
      </section>
    </>
  );
}

function PressPage() {
  const releaseEvent = events.find((event) => event.pressRelease);
  return (
    <>
      <section className="page-hero simple-hero">
        <div><p className="eyebrow">Press & media</p><h1>Coverage, context and public record.</h1></div>
        <p className="page-hero-copy">Independent features are separated from official listings. Every item links directly to its original source.</p>
      </section>
      {releaseEvent && (
        <section className="page-section press-release-download">
          <div>
            <p className="eyebrow">Latest press release</p>
            <h2>Contemporary Somali cinema in Venice</h2>
            <p>{releaseEvent.date} · {releaseEvent.location}</p>
          </div>
          <a className="button button-dark" href={releaseEvent.pressRelease} download>
            Download PDF <Download size={17} />
          </a>
        </section>
      )}
      <section className="page-section">
        <div className="press-list full-press-list">{pressCoverage.map((item) => <PressRow key={`${item.outlet}-${item.title}`} item={item} />)}</div>
      </section>
      <section className="press-contact-band">
        <div><p className="eyebrow light">Media enquiries</p><h2>Press resources and approved imagery</h2></div>
        <a className="button button-light" href={`mailto:${site.pressEmail}`}>Contact press office <Mail size={17} /></a>
      </section>
    </>
  );
}

function InstitutionGrid({ title, items }) {
  return (
    <div className="institution-group">
      <h3>{title}</h3>
      <div className="institution-grid">
        {items.map(([name, logo, url]) => {
          const content = logo ? <img src={logo} alt={name} loading="lazy" /> : <strong>{name}</strong>;
          return url ? <a key={name} href={url} target="_blank" rel="noreferrer" aria-label={`Visit ${name}`}>{content}</a> : <div key={name}>{content}</div>;
        })}
      </div>
    </div>
  );
}

function AboutPage() {
  return (
    <>
      <section className="page-hero simple-hero">
        <div><p className="eyebrow">About</p><h1>A national platform built for what follows.</h1></div>
        <p className="page-hero-copy">{about.governmentalPositioning[0]}</p>
      </section>

      <section className="page-section mandate-grid">
        <div><p className="eyebrow">Mandate</p><h2>International presentation must create value beyond representation.</h2></div>
        <div>
          <p>{about.governmentalPositioning[1]}</p>
          <p>The Pavilion presents contemporary Somali artistic practices while developing an archive, public programmes and institutional partnerships that continue beyond the Biennale. It works actively with Somalia’s arts community and supports cultural exchange, knowledge transfer and future institutional capacity at home.</p>
        </div>
      </section>

      <section className="page-section dark-section research-section">
        <div className="research-heading">
          <p className="eyebrow light">Development · {about.research.period}</p>
          <h2>{about.research.title}</h2>
        </div>
        <div className="research-body">
          {about.research.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          <div className="research-locations" aria-label="Research locations">
            {about.research.locations.map((location) => <span key={location}>{location}</span>)}
          </div>
        </div>
      </section>

      <section className="page-section foundations-section">
        <SectionIntro eyebrow="Institutional foundations" title="Work developed in Somalia" />
        <div className="foundation-grid">
          {about.foundations.map((project) => (
            <article key={project.title}>
              <p>{project.year}</p>
              <h3>{project.title}</h3>
              <span>{project.text}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="page-section muted-section leadership-section">
        <SectionIntro eyebrow="Leadership" title={<><span>Commissioner</span><span>and curators</span></>} />
        <div className="leadership-grid">
          {leadership.map((person) => <article key={person.name}><img src={person.image} alt={`Portrait of ${person.name}`} loading="lazy" /><p>{person.role}</p><h3>{person.name}</h3><span>{person.bio}</span></article>)}
        </div>
      </section>

      <section className="page-section team-section">
        <SectionIntro eyebrow="Team" title="Project and advisory team" />
        <div className="team-list">{team.map(([name, role]) => <div key={name}><strong>{name}</strong><span>{role}</span></div>)}</div>
      </section>

      <section className="page-section institutions-section">
        <SectionIntro eyebrow="Institutional network" title="Patronage, supporters and partners" />
        <InstitutionGrid title="Institutional patronage" items={institutions.patronage} />
        <InstitutionGrid title="Supporters" items={institutions.supporters} />
        <InstitutionGrid title="Exhibition partners" items={institutions.partners} />
      </section>
    </>
  );
}

function NotFound({ navigate }) {
  return (
    <section className="not-found">
      <p className="eyebrow">404</p><h1>This page is not part of the Pavilion archive.</h1>
      <Link to="/" navigate={navigate} className="button button-dark">Return home <ArrowRight size={17} /></Link>
    </section>
  );
}

function App() {
  const { path, navigate } = useClientRouter();

  const page = useMemo(() => {
    if (path === '/') return <HomePage navigate={navigate} />;
    if (path === '/exhibition') return <ExhibitionPage navigate={navigate} />;
    if (path === '/artists') return <ArtistsPage navigate={navigate} />;
    if (path === '/programme') return <ProgrammePage navigate={navigate} />;
    if (path === '/press') return <PressPage />;
    if (path === '/about') return <AboutPage />;

    const artist = artists.find((item) => path === `/artists/${item.slug}`);
    if (artist) return <ArtistPage artist={artist} navigate={navigate} />;

    const event = events.find((item) => path === `/programme/${item.slug}`);
    if (event) return <EventPage event={event} navigate={navigate} />;

    const rsvpEvent = events.find((item) => path === `/programme/${item.slug}/rsvp`);
    if (rsvpEvent) return <RsvpPage event={rsvpEvent} navigate={navigate} />;

    return <NotFound navigate={navigate} />;
  }, [navigate, path]);

  return <Layout path={path} navigate={navigate}>{page}</Layout>;
}

export default App;

